import { useCallback, useEffect, useState } from 'react'
import { usePersistentTab } from '../../hooks/usePersistentTab'
import { AlertCircle, FileText, Loader2, RefreshCcw, Server, Zap } from 'lucide-react'
import Notification from '../../components/Notification.jsx'
import { supabase } from '../../lib/supabaseClient'

const TABS = [{ key: 'pdf', label: 'PDF' }]

const DEFAULT_PROVIDERS = {
  pdfshift: { enabled: true },
  api2pdf: { enabled: false },
}

const PROVIDERS = [
  {
    key: 'pdfshift',
    name: 'PDFShift',
    icon: <FileText size={20} />,
    description: 'Provedor de conversão de HTML em PDF usado na emissão de certificados e documentos.',
  },
  {
    key: 'api2pdf',
    name: 'API2PDF',
    icon: <Server size={20} />,
    description: 'Provedor alternativo de geração de PDF. Quando ativo, é usado como alternativa caso o PDFShift falhe.',
  },
]

function Tabs({ active, onChange }) {
  return (
    <div className="border-b border-slate-800 mb-6 overflow-x-auto">
      <div className="flex gap-2 min-w-max">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={`px-4 py-3 text-sm font-bold uppercase tracking-wide transition border-b-2 ${
              active === t.key
                ? 'text-teal-400 border-teal-400'
                : 'text-slate-500 border-transparent hover:text-slate-300'
            }`}
            aria-current={active === t.key ? 'page' : undefined}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function ToggleSwitch({ checked, onChange, disabled, color, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onChange}
      className={`w-12 h-6 shrink-0 rounded-full relative transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
        checked ? '' : 'bg-slate-700'
      }`}
      style={checked ? { backgroundColor: color } : undefined}
    >
      <span
        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-300 shadow-sm ${
          checked ? 'left-7' : 'left-1'
        }`}
      />
    </button>
  )
}

function ProviderCard({ provider, state, saving, color, onToggle }) {
  const enabled = !!state?.enabled
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 shrink-0 rounded-lg bg-slate-950 border border-slate-800 flex items-center justify-center" style={{ color }}>
            {provider.icon}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-white font-bold">{provider.name}</h3>
            </div>
            <p className="text-slate-400 text-sm mt-1">{provider.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saving ? <Loader2 size={16} className="animate-spin text-slate-500" /> : null}
          <ToggleSwitch
            checked={enabled}
            onChange={() => onToggle(provider.key, !enabled)}
            disabled={saving}
            color={color}
            ariaLabel={`Ativar ou desativar ${provider.name}`}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 mt-5 pt-4 border-t border-slate-800 text-xs font-bold uppercase tracking-wide">
        <span className={enabled ? 'text-teal-400' : 'text-slate-500'}>{enabled ? 'Ativo' : 'Inativo'}</span>
        <span className="text-slate-700">•</span>
        <span className="text-slate-500">API {provider.name}</span>
      </div>
    </div>
  )
}

export default function AdminConnections({ partner }) {
  const [activeTab, setActiveTab] = usePersistentTab('admin_connections', 'pdf')
  const [providers, setProviders] = useState(DEFAULT_PROVIDERS)
  const [loading, setLoading] = useState(true)
  const [savingKey, setSavingKey] = useState('')
  const [error, setError] = useState('')
  const [notification, setNotification] = useState(null)

  const accentColor = partner?.color || '#f97316'

  const loadProviders = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const { data, error: rpcError } = await supabase.rpc('get_pdf_providers_config')
      if (rpcError) throw rpcError
      const row = Array.isArray(data) ? data[0] : data
      setProviders({
        pdfshift: { enabled: row?.pdfshift_enabled == null ? true : Boolean(row.pdfshift_enabled) },
        api2pdf: { enabled: Boolean(row?.api2pdf_enabled) },
      })
    } catch (err) {
      console.error('Erro ao carregar configuração dos provedores de PDF', err)
      setError(String(err?.message || 'Falha ao carregar as configurações.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadProviders()
  }, [loadProviders])

  const handleToggle = async (providerKey, nextEnabled) => {
    const provider = PROVIDERS.find((p) => p.key === providerKey)
    const previous = providers[providerKey]?.enabled
    setProviders((prev) => ({ ...prev, [providerKey]: { ...prev[providerKey], enabled: nextEnabled } }))
    setSavingKey(providerKey)
    try {
      const { error: rpcError } = await supabase.rpc('set_pdf_provider_enabled', {
        provider: providerKey,
        enable: nextEnabled,
      })
      if (rpcError) throw rpcError
      setNotification({
        message: `API ${provider?.name || providerKey} ${nextEnabled ? 'ativada' : 'desativada'} com sucesso!`,
        type: 'success',
      })
    } catch (err) {
      console.error('Erro ao atualizar provedor de PDF', err)
      setProviders((prev) => ({ ...prev, [providerKey]: { ...prev[providerKey], enabled: previous } }))
      setNotification({
        message: `Falha ao atualizar a API ${provider?.name || providerKey}.`,
        type: 'error',
      })
    } finally {
      setSavingKey('')
    }
  }

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      {notification ? (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      ) : null}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Integrações</h1>
          <p className="text-slate-400">Gerencie as integrações e APIs conectadas ao ecossistema {partner?.name || 'Sofera'}.</p>
        </div>
      </div>

      <Tabs active={activeTab} onChange={setActiveTab} />

      {activeTab === 'pdf' && (
        <div>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-white">Provedores de PDF</h2>
              <p className="text-slate-500 text-sm">Ative ou desative as APIs responsáveis pela geração de PDFs.</p>
            </div>
            <button
              type="button"
              onClick={loadProviders}
              disabled={loading}
              className="px-4 py-2.5 rounded-lg border border-slate-800 text-slate-300 hover:text-white hover:border-slate-700 transition inline-flex items-center gap-2 font-bold text-sm disabled:opacity-60"
            >
              <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} />
              Atualizar
            </button>
          </div>

          {error ? (
            <div className="bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="mt-0.5" size={18} />
              <div>
                <div className="font-bold">Falha ao carregar configurações</div>
                <div className="text-sm text-rose-200/90">{error}</div>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {PROVIDERS.map((provider) => (
              <ProviderCard
                key={provider.key}
                provider={provider}
                state={providers[provider.key]}
                saving={savingKey === provider.key}
                color={accentColor}
                onToggle={handleToggle}
              />
            ))}
          </div>

          <div className="mt-6 flex items-start gap-2 text-xs text-slate-500">
            <Zap size={14} className="mt-0.5 shrink-0" />
            <p>
              As alterações são aplicadas imediatamente, sem necessidade de publicação. O PDFShift é o provedor principal;
              em caso de falha, o API2PDF é acionado automaticamente quando estiver ativo.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
